import { FC, useMemo, useState } from 'react';
import { DropDownButton, IconName, Stack } from '@optimus-web/core';
import { ToggleButton } from '@mews/b2b-ui';
import { AddProductsToReservationPanel } from '../panels/AddProductsToReservationPanel/AddProductsToReservationPanel';
import { ClaimPerPersonPanel } from '../panels/ClaimPerPersonPanel/ClaimPerPersonPanel';
import { useProductCheckListFiltersContext } from '../../contexts/ProductCheckListFiltersContext';
import { useToggleClaimAll } from '../../utils/useToggleClaimAll';
import { ProductCheckListRowAction } from '../../containers/ProductCheckListTable/types';
import { ProductCheckListDataRow } from '../../types';
import { ProductCheckListDataTestIds } from '../../constants';
import { useActionCellMenuOptions } from './utils/useActionCellMenuOptions';
import { useActionCellGetOptionDropdownItem } from './utils/useActionCellGetOptionDropdownItem';
import { useFilteredProductsData } from './utils/useFilteredProductsData';

interface ActionCellProps {
    row: ProductCheckListDataRow;
}

export const ActionCell: FC<ActionCellProps> = ({ row }) => {
    const [panel, setPanel] = useState<ProductCheckListRowAction | null>(null);

    const { addProductsMenuOptions, dotsMenuOptions } = useActionCellMenuOptions(row);

    const filteredProducts = useFilteredProductsData(row.productsData);

    const allFilteredProductsClaimed = useMemo(
        () => filteredProducts.every(({ claimed, totalCount }) => claimed === totalCount),
        [filteredProducts]
    );

    const getOptionDropdownItem = useActionCellGetOptionDropdownItem(row, setPanel);

    const { filters } = useProductCheckListFiltersContext();

    const { toggleClaimAll, isLoading } = useToggleClaimAll(filteredProducts, allFilteredProductsClaimed);

    const showAddProductsMenu = !!addProductsMenuOptions?.length;

    const showClaimAllButton = !showAddProductsMenu && !!row.productsData.length;

    const closePanel = () => {
        setPanel(null);
    };

    return (
        <Stack itemAlignment="end" spacing="200" data-test-id={ProductCheckListDataTestIds.cellActions}>
            {showAddProductsMenu && (
                <DropDownButton icon={IconName.Add} size="small" variant="tertiary">
                    {addProductsMenuOptions.map(getOptionDropdownItem)}
                </DropDownButton>
            )}

            {showClaimAllButton && (
                <ToggleButton
                    size="small"
                    checked={allFilteredProductsClaimed}
                    onClick={toggleClaimAll}
                    loading={isLoading}
                    data-test-claim-all={!row.allClaimed}
                    compact
                    type="submit"
                />
            )}

            <DropDownButton icon={IconName.More} size="small" variant="ghost">
                {dotsMenuOptions.map(getOptionDropdownItem)}
            </DropDownButton>

            {row.serviceId && (
                <AddProductsToReservationPanel
                    opened={panel === ProductCheckListRowAction.AddProductsToReservation}
                    onClose={closePanel}
                    owner={row.owner}
                    resourceId={row.resourceId}
                    serviceId={row.serviceId}
                    reservationId={row.reservation?.id}
                    defaultValues={{
                        consumedUtc: filters.date,
                    }}
                />
            )}

            <ClaimPerPersonPanel
                opened={panel === ProductCheckListRowAction.ClaimPerPerson}
                onClose={closePanel}
                row={row}
            />
        </Stack>
    );
};
